/* -------------------------------------------------------------------------- */
/* 1. Les registres                                                           */
/* -------------------------------------------------------------------------- */
/** L'état d'une séance de tutorat — `SeanceTutorat.etat`. */
export const ETATS_SEANCE_TUTORAT = ['a_venir', 'faite', 'annulee'];
export const LIBELLES_ETAT_SEANCE = {
    a_venir: 'À venir',
    faite: 'Faite',
    annulee: 'Annulée',
};
/** Durée d'une séance : de 15 à 180 minutes, par pas de 15 (MV-TUT-05). */
export const DUREE_SEANCE_MIN = 15;
export const DUREE_SEANCE_MAX = 180;
export const DUREE_SEANCE_PAS = 15;
export const DUREE_SEANCE_PAR_DEFAUT = 60;
export const MAX_CARACTERES_NOTE_SEANCE = 4_000;
/**
 * D'où l'élève a ouvert une fiche ou une capsule — `ConsultationRessource.origine`.
 * `parcours` (lot P1, AB#973) : une étape d'un parcours. Ajouté À LA FIN.
 */
export const ORIGINES_CONSULTATION = [
    'remede',
    'libre',
    'sequence_tuteur',
    'preparation',
    'parcours',
];
/** Ce qu'une consultation désigne : jamais un exercice (c'est sa correction qui le trace). */
export const GENRES_DE_CONSULTATION = ['fiche', 'capsule'];
/** Où en est une séquence — CALCULÉ, jamais stocké. */
export const ETATS_SEQUENCE = ['en_cours', 'echue', 'terminee', 'retiree'];
/** Bornes de ce qu'une séquence accepte. */
export const MAX_ELEMENTS_SEQUENCE = 20;
export const MAX_CARACTERES_TITRE_SEQUENCE = 80;
export const MAX_CARACTERES_MOT_SEQUENCE = 600;
export const MAX_CARACTERES_RAISON_ELEMENT = 200;
/**
 * Le niveau maximal qu'un tuteur peut poser (F-14 ; `DECISIONS-ARCHITECTE.md`,
 * point 4). `null` — aucun réglage — est le défaut : tout est ouvert. Le
 * niveau 4 n'y est pas : « limiter au niveau 4 » ne limiterait rien.
 */
export const NIVEAUX_MAXIMAUX = [1, 2, 3];
/** Le refus NOMMÉ d'un exercice au-delà du niveau maximal : 403, ce code. */
export const CODE_NIVEAU_LIMITE = 'NIVEAU_LIMITE_PAR_LE_TUTEUR';
/** Un élément refusé au dépôt d'une séquence : 422, ce code, la raison dans `message`. */
export const CODE_SEQUENCE_INVALIDE = 'SEQUENCE_INVALIDE';
/** Une séquence retirée ne se modifie plus : 409. */
export const CODE_SEQUENCE_RETIREE = 'SEQUENCE_RETIREE';
export const CODE_SEANCE_INVALIDE = 'SEANCE_INVALIDE';
/**
 * La tendance d'une notion sur sept jours. La BAISSE n'est dite qu'au tuteur :
 * le portrait de l'élève n'a que `enHausse` (MV-UX-03).
 */
export const TENDANCES_NOTION = ['hausse', 'stable', 'baisse'];
export const LIBELLES_TENDANCE = {
    hausse: 'En hausse',
    stable: 'Stable',
    baisse: 'En baisse',
};
/** Les deux compétences évaluées à l'école, et leur part SCOLAIRE — une donnée du programme, pas une mesure. */
export const COMPETENCES_AU_PROGRAMME = {
    'MAT-C2': { nom: 'Déployer un raisonnement mathématique', partScolaire: '70 % du résultat' },
    'MAT-C1': { nom: 'Résoudre une situation-problème', partScolaire: '30 % du résultat' },
};
/** Une trace de consultation écarte sa ressource du prochain geste pendant tant de jours. */
export const JOURS_SANS_REPROPOSER = 7;
/** Le titre de la section des erreurs sur la fiche d'un élève (`UX-7-13`). */
export const TITRE_ERREURS_RELEVEES = 'Erreurs relevées';
/* -------------------------------------------------------------------------- */
/* 3. L'objectif, posé par le tuteur                                          */
/* -------------------------------------------------------------------------- */
/**
 * POST /v1/tuteur/manuel/eleves/:eleveId/objectif — corps `PoserObjectifBody`,
 * réponse 201 `ObjectifResponse` (`manuel-objectif.ts`, § 2). MÊMES règles et
 * mêmes refus que la route de l'élève ; l'objectif porte `definiPar: 'tuteur'`.
 * Un objectif actif de la même matière est clos dans la transaction.
 *
 * PATCH /v1/tuteur/manuel/objectifs/:id — corps `ModifierObjectifBody`, réponse
 * `ObjectifResponse`. L'auteur d'origine n'est pas réécrit : le tuteur modifie
 * aussi un objectif que l'élève a posé. 404 objectif inconnu ; 403 élève non
 * suivi ; 409 `OBJECTIF_CLOS` ; 422 `OBJECTIF_INVALIDE`.
 */
export const ROUTES_OBJECTIF_TUTEUR = {
    poser: 'POST /v1/tuteur/manuel/eleves/:eleveId/objectif',
    modifier: 'PATCH /v1/tuteur/manuel/objectifs/:id',
};
/* -------------------------------------------------------------------------- */
/* 10. La liste des élèves, par « pourquoi maintenant » — GET /v1/tuteur/manuel/eleves */
/* -------------------------------------------------------------------------- */
/**
 * Les SEPT SIGNAUX de la liste (MV-TUT-01, AD-MV-21). Chacun est un FAIT daté
 * et contestable ; le serveur les pèse, ordonne les élèves, et écrit le motif.
 * L'écran ne porte aucune règle de priorité : il rend l'ordre reçu.
 *
 * Aucun des sept ne suppose une cohorte ni une agence : tous sont portés.
 */
export const SIGNAUX_DE_PRIORITE = [
    'evaluation',
    'amont',
    'erreur',
    'inactif',
    'sans-objectif',
    'sequence',
    'baisse',
];
/** Le libellé du FILTRE de chaque signal — l'étiquette d'une ligne, elle, est datée (« Évaluation dans 6 jours »). */
export const LIBELLES_SIGNAL_DE_PRIORITE = {
    evaluation: 'Évaluation proche',
    amont: 'Blocage en amont',
    erreur: 'Erreur récurrente',
    inactif: 'Inactivité',
    'sans-objectif': 'Objectif à définir',
    sequence: 'Séquence échue',
    baisse: 'Réussite en baisse',
};
/** Ce que la ligne dit d'un élève sans aucun signal. */
export const PHRASE_SANS_SIGNAL = 'Rien à signaler : travail régulier, préparation en ordre.';
/**
 * Ce que la ligne dit d'un élève dont le consentement au suivi pédagogique
 * n'est pas en vigueur — jamais accordé, ou à renouveler depuis une nouvelle
 * version du document. L'écran l'affiche à la place du motif.
 */
export const MOTIF_SANS_CONSENTEMENT = 'Consentement de la famille au suivi pédagogique à obtenir ou à renouveler : rien de son travail n’est affiché d’ici là.';
/* -------------------------------------------------------------------------- */
/* 11. La préparation de séance — GET /v1/tuteur/manuel/eleves/:eleveId/preparation-seance */
/* -------------------------------------------------------------------------- */
/**
 * Une priorité vient d'un blocage en amont ou d'une erreur revenue.
 * `evaluation` n'est plus rendue (AB#977) ; elle reste au registre.
 */
export const GENRES_DE_PRIORITE = ['amont', 'erreur', 'evaluation'];
/** Trois priorités AU PLUS — et deux réelles valent mieux que trois (règle R3). */
export const MAX_PRIORITES_DE_SEANCE = 3;
/** Ce que la page dit quand il n'y a aucune priorité : aucune carte n'est fabriquée. */
export const PHRASE_SANS_PRIORITE = 'Aucune erreur récurrente ni évaluation à préparer. Avancer sur la notion du moment.';
/* -------------------------------------------------------------------------- */
/* 12. « Travaux et corrections » — GET /v1/tuteur/manuel/eleves/:eleveId/travaux */
/* -------------------------------------------------------------------------- */
export const LIBELLES_GENRE_DE_TRAVAIL = {
    exercice: 'Exercice',
    defi: 'Défi',
    devoir: 'Devoir photographié',
};
/** D'où venait un essai d'exercice, tel que le tuteur le lit. */
export const LIBELLES_ORIGINE_DE_TRAVAIL = {
    entrainement: 'Entraînement',
    sequence_tuteur: 'Séquence du tuteur',
    aide_aux_devoirs: 'Aide aux devoirs',
    capsule: 'Après une capsule',
    parcours: 'Depuis un parcours',
};
export const TRAVAUX_PAR_PAGE_DEFAUT = 30;
export const TRAVAUX_PAR_PAGE_MAX = 100;
/* -------------------------------------------------------------------------- */
/* 13. « Évolution » — GET /v1/tuteur/manuel/eleves/:eleveId/evolution        */
/* -------------------------------------------------------------------------- */
/** L'évolution se lit sur cinq semaines, du lundi au dimanche. */
export const SEMAINES_D_EVOLUTION = 5;
/* -------------------------------------------------------------------------- */
/* 14. Le rapport unique — GET /v1/tuteur/manuel/eleves/:eleveId/rapport      */
/* -------------------------------------------------------------------------- */
/** Le titre de la section que le chapitre ajoute au document de la famille. */
export const TITRE_CHAPITRE_DU_RAPPORT = 'Le chapitre en cours';
/** La mention de la section : les quatre repères, et ce qu'ils ne disent pas. */
export const MENTION_CHAPITRE_DU_RAPPORT = 'Les quatre repères — nouveau, en progression, à consolider, acquis — décrivent ce qui est réussi dans les exercices. Aucun résultat scolaire n’est prédit.';
//# sourceMappingURL=manuel-tuteur.js.map